'use client';

import classNames from 'classnames';
import { notFound, useRouter, useSearchParams } from 'next/navigation';
import { useValRoute } from '../../../val/val.client';
import { Header } from '../../../components/Header/Header';
import ModalLayout from '../../../containers/ModalLayout';
import { BlankRectangle } from '../../../components/SVGs';
import { prosjektpratSchema } from './prosjektpratSchema';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Suspense } from 'react';
import Button from '../../../components/Button/Button';
import { Cross } from '../../../components/SVGs';
import pageVal from './page.val';

const Project = ({ params }: { params: Promise<never> }) => {
  return (
    <Suspense fallback={null}>
      <ProjectMain params={params} />
    </Suspense>
  );
};

type ContactInfoProps = {
  name: string;
  job: string;
  email: string;
};

const ContactInfo = ({ name, job, email }: ContactInfoProps) => {
  return (
    <div className="flex flex-col gap-4">
      <div className="font-solina font-extralight text-xl/[150%]">{name}</div>
      <div className="font-solina font-extralight text-base/[150%]">{job}</div>
      <div className="font-solina font-extralight text-base/[150%]">
        <a href={`mailto:${email}`}>{email}</a>
      </div>
    </div>
  );
};

const ProjectMain = ({ params }: { params: Promise<never> }) => {
  const page = useValRoute(pageVal, params);

  const searchParams = useSearchParams();
  const router = useRouter();

  const isFormSubmitted = searchParams?.get('submitted') === 'true';
  const isSubmitSuccess = searchParams?.get('success') === 'true';

  type FormData = z.infer<typeof prosjektpratSchema>;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(prosjektpratSchema),
  });

  const onSubmit = async (data: FormData) => {
    const res = await fetch('/api/contact', {
      method: 'POST',
      headers: {
        Accept: 'application/json, text/plain, */*',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ ...data, isProjectRelated: true }),
    });

    const params = new URLSearchParams(searchParams?.toString() ?? undefined);
    params.set('submitted', 'true');
    params.set('success', res.ok ? 'true' : 'false');
    router.push(`?${params.toString()}`);
  };

  const handleClose = () => {
    reset();
    if (
      typeof window !== 'undefined' &&
      document.referrer.includes('blank.no')
    ) {
      router.back();
      return;
    }

    window.location.href = 'https://www.blank.no/';
  };

  const inputBase =
    'w-full field-sizing-content border border-blank-yellow text-lg p-8 focus:outline-none focus:border focus:border-blank-blue-secondary';
  const errorBase = 'text-danger-primary-light text-sm mt-4';

  if (!page) {
    return notFound();
  }
  const {
    title,
    description,
    email,
    phone,
    placeholders,
    formSuccess,
    formError,
  } = page;

  return (
    <>
      <div
        id="prosjektarbeid"
        className={classNames(
          'bg-blank-yellow text-blank-black',
          'overflow-auto',
          'h-screen'
        )}
      >
        <Header>
          <div>
            <button
              className="pointer-events-auto cursor-pointer"
              onClick={handleClose}
              aria-label="Lukk prosjektprat"
            >
              <Cross />
            </button>
          </div>
        </Header>

        <ModalLayout>
          <div
            className={classNames(
              'w-full',
              'flex flex-col tablet:flex-row',
              'gap-64 laptop:gap-24'
            )}
          >
            {!isFormSubmitted ? (
              <form
                onSubmit={handleSubmit(onSubmit)}
                className="flex flex-col gap-32 w-full"
              >
                <div className="space-y-[16px]">
                  <h1 className="font-respira text-[40px]">{title}</h1>
                  <p className="text-[20px] text-pretty">{description}</p>
                </div>

                <div className="flex flex-col gap-8">
                  <label htmlFor="email">E-post</label>
                  <input
                    id="email"
                    type="text"
                    placeholder={placeholders.email}
                    {...register('email')}
                    className={classNames(
                      inputBase,
                      errors.email
                        ? 'border-b-danger-primary-light'
                        : 'border-b-blank-black'
                    )}
                  />
                  {errors.email && (
                    <p className={errorBase}>{errors.email.message}</p>
                  )}
                </div>
                <div className="flex flex-col gap-8">
                  <label htmlFor="phone">Telefonnummer</label>
                  <input
                    id="phone"
                    type="tel"
                    placeholder={placeholders.telephone}
                    {...register('phone')}
                    className={classNames(
                      inputBase,
                      errors.phone
                        ? 'border-b-danger-primary-light'
                        : 'border-b-blank-black'
                    )}
                  />
                  {errors.phone && (
                    <p className={errorBase}>{errors.phone.message}</p>
                  )}
                </div>

                <div className="flex flex-col gap-8">
                  <label htmlFor="message">Hva kan vi hjelpe deg med?</label>
                  <textarea
                    id="message"
                    placeholder={placeholders.message}
                    {...register('projectDetails')}
                    className={classNames(
                      inputBase,
                      errors.projectDetails
                        ? 'border-b-danger-primary-light'
                        : 'border-b-blank-black'
                    )}
                  />
                  {errors.projectDetails && (
                    <p className={errorBase}>{errors.projectDetails.message}</p>
                  )}
                </div>

                <div className="flex flex-col tablet:flex-row gap-16">
                  <Button
                    withArrow
                    scheme="light"
                    isDisabled={isSubmitting}
                    type="submit"
                  >
                    Send inn
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    scheme="light"
                    isDisabled={isSubmitting}
                    onClick={handleClose}
                  >
                    Avbryt
                  </Button>
                </div>
              </form>
            ) : (
              <div className="flex flex-col gap-32 w-full">
                <div className="font-respira text-[40px]/[130%]">
                  {isSubmitSuccess ? formSuccess.header : formError.header}
                </div>
                <div className="font-solina font-extralight text-xl/[150%]">
                  {isSubmitSuccess ? formSuccess.text : formError.text + ' '}
                  {!isSubmitSuccess && (
                    <a href={`mailto:${formError.mail}`}>{formError.mail}</a>
                  )}
                </div>

                <Button
                  onClick={handleClose}
                  variant="secondary"
                  scheme="light"
                >
                  Lukk
                </Button>
              </div>
            )}

            {/* Contact */}
            <div
              className={classNames(
                'flex flex-col gap-32',
                'w-full',
                'tablet:text-right'
              )}
            >
              <div
                className={classNames(
                  'flex flex-col gap-4',
                  'font-respira',
                  'text-[40px]/[130%]'
                )}
              >
                <div>
                  <a href={`mailto:${email}`}>{email}</a>
                </div>
                <div>
                  <a href={`tel:${phone}`}>
                    {phone
                      .replace(' ', '')
                      .replace(/(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4')}
                  </a>
                </div>
              </div>
              <ContactInfo
                name="Jahn Arne Johnsen"
                job="Daglig leder"
                email="jaj@blank.no"
              />
              <ContactInfo
                name="Knut Backer"
                job="Salgssjef"
                email="kb@blank.no"
              />
            </div>
          </div>
        </ModalLayout>
        <div className="mt-32 py-24 px-16 laptop:px-24">
          <BlankRectangle color="var(--dark)" />
        </div>
      </div>
    </>
  );
};

export default Project;
